import { Button, Box, SxProps } from '@mui/material';
import clsx from 'clsx';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';

import styles from './AdvisorProfileCardSlides.module.css';
import {
  useIsPortraitTablet,
  useIsTablet,
} from 'hooks/useBreakpoints/useBreakpoints';
import AdvisorProfileSummaryCard from './AdvisorProfileSummaryCard';
import AdvisorProfileAboutMeCard from './AdvisorProfileAboutMeCard';
import { Advisor } from 'types';
import AdvisorProfileSocialCard from './AdvisorProfileSocialCard';
import AdvisorProfileBusinessCard from './AdvisorProfileBusinessCard';
import AdvisorProfileContactCard from './AdvisorProfileContactCard';
import AdvisorProfileTeamCard from './AdvisorProfileTeamCard';
import { useAdvisorProfileTheme } from './AdvisorProfileThemeProvider';
import { PaidFeatureAlertType } from '../PaidFeatureAlert/PaidFeatureAlert.types';

interface Props {
  isDemo: boolean;
  isFreeUser: boolean;
  advisor?: Advisor;
  onShowShortcut?: () => void;
  children?: React.ReactNode;
  paidFeatureAlertType: PaidFeatureAlertType;
  cardSx?: SxProps;
}

export default forwardRef((props: Props, ref) => {
  const {
    advisor,
    onShowShortcut,
    children,
    isFreeUser,
    paidFeatureAlertType,
    isDemo,
    cardSx,
  } = props;
  const isTablet = useIsTablet();

  const swiperRef = useRef<any>(null);
  const onSwiper = swiper => (swiperRef.current = swiper);
  const [slideIndex, setSlideIndex] = useState(0);
  const onSlideChange = swiper => {
    setSlideIndex(swiper.realIndex);
  };

  const { mainColor, color4 } = useAdvisorProfileTheme();

  const navButtonSx = {
    minWidth: 0,
    minHeight: 0,
    padding: 0,
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    backgroundColor: mainColor,
    '&:hover': {
      backgroundColor: color4,
    },
  };

  const shouldShowProfilePlusTeamPage =
    advisor?.adviserInfo.shouldShowProfilePlusTeamPage ?? false;
  const isPortraitTablet = useIsPortraitTablet();

  useImperativeHandle(
    ref,
    () => ({
      swiper: swiperRef.current,
    }),
    [swiperRef],
  );

  return (
    <Box className={styles.container} sx={cardSx}>
      <Box className={styles.cardsContainer}>
        <Button
          variant="contained"
          className={clsx(styles.swiperButtonPrev)}
          sx={navButtonSx}
          onClick={() => swiperRef.current?.slidePrev()}
          disabled={!advisor}
        >
          <KeyboardArrowLeftIcon fontSize="small" />
        </Button>
        <Swiper
          initialSlide={0}
          slidesPerView={isTablet ? (isPortraitTablet ? 1.5 : 1.15) : 1.35}
          fadeEffect={{
            crossFade: true,
          }}
          loop={true}
          loopedSlides={0}
          centeredSlides={true}
          centeredSlidesBounds={true}
          autoHeight={true}
          touchStartPreventDefault={false}
          effect="coverflow"
          autoplay={{
            delay: 12000,
          }}
          coverflowEffect={{
            rotate: 0,
            depth: 350,
            modifier: 2,
            stretch: 75,
            slideShadows: false,
          }}
          pagination={{
            el: '.swiper-pagination',
            type: 'bullets',
            clickable: true,
          }}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          onSwiper={onSwiper}
          onSlideChange={onSlideChange}
          allowSlidePrev={!!advisor}
          allowSlideNext={!!advisor}
          style={{
            flexGrow: 1,
            padding: '6px 0px',
            height: '100%',
          }}
        >
          {isTablet && (
            <SwiperSlide className={clsx(styles.swiperSlideContainer)}>
              <AdvisorProfileSummaryCard
                advisor={advisor}
                onShowShortcut={onShowShortcut}
                isFreeUser={isFreeUser}
                paidFeatureAlertType={paidFeatureAlertType}
                isDemo={isDemo}
                shakeOnWhatsapp={slideIndex === 0}
              />
            </SwiperSlide>
          )}
          <SwiperSlide className={clsx(styles.swiperSlideContainer)}>
            <AdvisorProfileAboutMeCard
              advisor={advisor}
              isDemo={isDemo}
              isFreeUser={isFreeUser}
            />
          </SwiperSlide>
          <SwiperSlide className={clsx(styles.swiperSlideContainer)}>
            <AdvisorProfileSocialCard
              advisor={advisor}
              isDemo={isDemo}
              isFreeUser={isFreeUser}
            />
          </SwiperSlide>
          <SwiperSlide className={clsx(styles.swiperSlideContainer)}>
            <AdvisorProfileBusinessCard
              advisor={advisor}
              isDemo={isDemo}
              isFreeUser={isFreeUser}
            />
          </SwiperSlide>
          <SwiperSlide className={clsx(styles.swiperSlideContainer)}>
            <AdvisorProfileContactCard
              advisor={advisor}
              isDemo={isDemo}
              isFreeUser={isFreeUser}
            />
          </SwiperSlide>
          {shouldShowProfilePlusTeamPage && (
            <SwiperSlide className={clsx(styles.swiperSlideContainer)}>
              <AdvisorProfileTeamCard
                advisor={advisor}
                isDemo={isDemo}
                isFreeUser={isFreeUser}
              />
            </SwiperSlide>
          )}
        </Swiper>
        <Button
          variant="contained"
          className={clsx(styles.swiperButtonNext)}
          sx={navButtonSx}
          onClick={() => swiperRef.current?.slideNext()}
          disabled={!advisor}
        >
          <KeyboardArrowRightIcon fontSize="small" />
        </Button>
      </Box>
      {children}
    </Box>
  );
});
